import { useQuery } from '@tanstack/react-query';
import type { AxiosRequestConfig } from 'axios';
import { apiWrapper } from '@/apiWrapper';
import { getEndpoints } from '@/endpoints';
import useFeatureFlagsStore from '@/store/FeatureFlagsStore';
import type { RelevanceSearchResult } from '../types/RelevanceSearchResult';

type RelevanceSearchApiResponse = {
  results: (Omit<RelevanceSearchResult, 'id'> & {
    _id: string;
  })[];
};

export const relevanceSearchQueryKey = (
  search: string,
  useNewDest?: boolean,
  useGoogleApiForRBS?: boolean,
) => ['relevanceSearch', search, useNewDest, useGoogleApiForRBS] as const;

export const getRelevanceSearch = async (
  search: string,
  useNewDest?: boolean,
  useGoogleApiForRBS?: boolean,
): Promise<RelevanceSearchResult[]> => {
  const url = getEndpoints().relevanceSearch;
  const config: AxiosRequestConfig = {
    method: 'GET',
    params: {
      q: search,
      ...(useNewDest && { useNewDest: 'true' }),
      ...(useGoogleApiForRBS && { provider: 'google' }),
    },
  };

  const { results } = await apiWrapper<RelevanceSearchApiResponse>(url, config);
  return results.map((e) => ({
    ...e,
    id: e._id,
  }));
};

export const useRelevanceSearch = (search: string) => {
  const useNewDest = useFeatureFlagsStore(
    (state) => state.featureFlags.useNewDestinations,
  );
  const useGoogleApiForRBS = useFeatureFlagsStore(
    (state) => state.featureFlags.useGoogleApiForRBS,
  );

  return useQuery({
    queryKey: relevanceSearchQueryKey(search, useNewDest, useGoogleApiForRBS),
    queryFn: () =>
      search ? getRelevanceSearch(search, useNewDest, useGoogleApiForRBS) : [],
    staleTime: Number.POSITIVE_INFINITY,
  });
};
